import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PortalLayout } from "@/components/portal-layout";
import { HELP_CONTENT, ROLE_INTRO, getAdminWelcomeTopic, type HelpTopic } from "@/lib/help-content";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Lightbulb, Pin } from "lucide-react";
import { useEntitlements } from "@/hooks/use-entitlements";

export const Route = createFileRoute("/admin/help")({ component: () => <HelpHub portal="admin" /> });

export function HelpHub({ portal }: { portal: "admin" | "teacher" | "student" }) {
  const intro = ROLE_INTRO[portal];
  const ent = useEntitlements();
  const isAdminTeacher = portal === "admin" && ent.maxTeachers === 0;

  const items = Object.entries(HELP_CONTENT)
    .filter(([key]) => key.startsWith(`/${portal}`) || (portal === "admin" && key === "/billing"));

  const welcome: HelpTopic | null = portal === "admin" ? getAdminWelcomeTopic(isAdminTeacher) : null;

  return (
    <PortalLayout portal={portal} title="Hướng dẫn sử dụng">
      <div className="mb-6 rounded-2xl bg-card border p-6">
        <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">{intro.subtitle}</h2>
        <p className="text-muted-foreground mt-2 max-w-2xl">{intro.desc}</p>
      </div>

      {welcome && (
        <div className="mb-4 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-500/10 dark:to-yellow-500/5 p-5">
          <div className="flex items-start gap-3">
            <div className="size-9 shrink-0 rounded-xl bg-amber-100 dark:bg-amber-500/20 grid place-items-center">
              <Pin className="size-5 text-amber-600 dark:text-amber-300" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display font-semibold text-slate-900 dark:text-white">{welcome.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{welcome.summary}</p>
              {welcome.steps && (
                <ol className="mt-3 space-y-1.5 text-sm text-slate-700 dark:text-slate-200 list-decimal pl-5">
                  {welcome.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              {welcome.tips && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-400 list-disc pl-5">
                  {welcome.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <Accordion type="multiple" className="bg-card border rounded-2xl divide-y">
        {items.map(([key, topic]) => (
          <AccordionItem value={key} key={key} className="border-0 px-4">
            <AccordionTrigger className="hover:no-underline py-4">
              <div className="flex items-start gap-3 text-left">
                <div className="size-8 shrink-0 rounded-lg bg-amber-100 dark:bg-amber-500/20 grid place-items-center">
                  <Lightbulb className="size-4 text-amber-600 dark:text-amber-300" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-slate-900 dark:text-white">{topic.title}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{topic.summary}</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5 pl-11">
              {topic.steps && (
                <ol className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200 list-decimal pl-5">
                  {topic.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              {topic.tips && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-400 list-disc pl-5">
                  {topic.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PortalLayout>
  );
}
